import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { dateLabel, dayLabel, frequencyLabel, lastChangeLabel, methodLabel, sourceLabel, usedByLabel } from './setupSummary';

/** What the summary calls each field a setup change names (LB-6180), in the summary's order. */
const FIELD_LABELS = {
  billSource: 'How bills reach Livable',
  billToResidents: 'Bill to residents',
  allocationMethod: 'Allocation method',
  billFrequencyType: 'Bill frequency',
  isApPushEnabled: 'AP Push',
  name: 'Name',
  isArchived: 'Archived'
};
const FIELD_ORDER = Object.keys(FIELD_LABELS);
const onOff = value => value ? 'On' : 'Off';

/** A field's value as the summary words it. */
export const fieldValue = (field, value) => {
  switch (field) {
    case 'billSource':
      return sourceLabel(value);
    case 'billToResidents':
    case 'isApPushEnabled':
      return onOff(value);
    case 'allocationMethod':
      return methodLabel(value);
    case 'billFrequencyType':
      return frequencyLabel(value);
    case 'isArchived':
      return value ? 'Yes' : 'No';
    default:
      return value !== null && value !== void 0 ? value : '';
  }
};
const ordered = changes => [...(changes !== null && changes !== void 0 ? changes : [])].sort((a, b) => FIELD_ORDER.indexOf(a.field) - FIELD_ORDER.indexOf(b.field));
const changeOf = (entry, field) => {
  var _entry$changes;
  return (_entry$changes = entry.changes) === null || _entry$changes === void 0 ? void 0 : _entry$changes.find(change => change.field === field);
};

// "Edited by Sam Lee, Oct 1, 2026"; with no name recorded, "Edited, Oct 1, 2026".
const byLine = (verb, name, on) => "".concat(verb).concat(name ? " by ".concat(name) : '', ", ").concat(dateLabel(on));

/** What an applied entry did: its creation, a rename, archiving, or an edit of its values. */
const kindOf = entry => {
  var _entry$changes2, _changeOf;
  if (entry.created) return 'created';
  const fields = ((_entry$changes2 = entry.changes) !== null && _entry$changes2 !== void 0 ? _entry$changes2 : []).map(change => change.field);
  if (fields.length === 1 && fields[0] === 'name') return 'renamed';
  if (fields.length === 1 && fields[0] === 'isArchived') return (_changeOf = changeOf(entry, 'isArchived')) !== null && _changeOf !== void 0 && _changeOf.to ? 'archived' : 'restored';
  return 'edited';
};
const VERBS = {
  created: 'Created',
  renamed: 'Renamed',
  archived: 'Archived',
  restored: 'Restored',
  edited: 'Edited'
};

/**
 * The summary's Last change row (PRD V3 mockup): "Edited by Sam Lee, Oct 1, 2026: AP Push", from the newest change the
 * setup took. Until the history is read, or when it can't be, the setup's own stamp, as before.
 */
export const lastChangeLine = (setup, history) => {
  var _history$entries;
  const entry = history === null || history === void 0 || (_history$entries = history.entries) === null || _history$entries === void 0 ? void 0 : _history$entries.find(item => item.status === 'Applied');
  if (!entry) return setup ? lastChangeLabel(setup) : null;
  const kind = kindOf(entry);
  const line = byLine(VERBS[kind], entry.changedByName, entry.changedOn);
  if (kind !== 'edited') return line;
  const fields = ordered(entry.changes).map(change => {
    var _FIELD_LABELS$change$;
    return (_FIELD_LABELS$change$ = FIELD_LABELS[change.field]) !== null && _FIELD_LABELS$change$ !== void 0 ? _FIELD_LABELS$change$ : change.field;
  });
  return fields.length ? "".concat(line, ": ").concat(fields.join(', ')) : line;
};
const fieldLines = changes => ordered(changes).filter(change => change.field !== 'name' && change.field !== 'isArchived').map(change => {
  var _FIELD_LABELS$change$2;
  return "".concat((_FIELD_LABELS$change$2 = FIELD_LABELS[change.field]) !== null && _FIELD_LABELS$change$2 !== void 0 ? _FIELD_LABELS$change$2 : change.field, ": ").concat(fieldValue(change.field, change.from), " \u2192 ").concat(fieldValue(change.field, change.to));
});
const renameLine = entry => {
  var _rename$from, _rename$to;
  const rename = changeOf(entry, 'name');
  return rename && !entry.created ? "Name: ".concat((_rename$from = rename.from) !== null && _rename$from !== void 0 ? _rename$from : '', " \u2192 ").concat((_rename$to = rename.to) !== null && _rename$to !== void 0 ? _rename$to : '') : null;
};

// The values a setup was created with, on one line.
const createdLine = entry => {
  const value = field => {
    var _changeOf2;
    return (_changeOf2 = changeOf(entry, field)) === null || _changeOf2 === void 0 ? void 0 : _changeOf2.to;
  };
  return [sourceLabel(value('billSource')), "Bill to residents ".concat(onOff(value('billToResidents'))), methodLabel(value('allocationMethod')), frequencyLabel(value('billFrequencyType')), "AP Push ".concat(onOff(value('isApPushEnabled')).toLowerCase())].join(' · ');
};
const scheduledLine = entry => {
  if (!entry.effectiveFrom) return null;
  return "Applied from ".concat(dayLabel(entry.effectiveFrom)).concat(entry.scheduledOn ? ", scheduled ".concat(dateLabel(entry.scheduledOn)) : '');
};

/**
 * The setup's history as the Setup section lists it, newest first: a heading for each change and its details. Edits
 * that were scheduled and never applied are listed with what they would have changed.
 */
export const setupEntries = history => {
  var _history$entries2;
  return ((_history$entries2 = history === null || history === void 0 ? void 0 : history.entries) !== null && _history$entries2 !== void 0 ? _history$entries2 : []).map((entry, index) => {
    const key = "".concat(entry.changedOn, "|").concat(entry.status, "|").concat(index);
    const outcome = entry.outcome ? [entry.outcome] : [];
    if (entry.status !== 'Applied') {
      const verb = entry.status === 'Failed' ? 'failed' : 'cancelled';
      const edit = entry.effectiveFrom ? "Edit for ".concat(dayLabel(entry.effectiveFrom)) : 'Scheduled edit';
      return {
        key,
        heading: "".concat(edit, " ").concat(verb, ", ").concat(dateLabel(entry.changedOn)).concat(entry.changedByName ? " \xB7 ".concat(entry.changedByName) : ''),
        lines: [...fieldLines(entry.changes), renameLine(entry), ...outcome].filter(Boolean)
      };
    }
    const kind = kindOf(entry);
    const heading = byLine(VERBS[kind], entry.changedByName, entry.changedOn);
    if (kind === 'created') {
      const followed = entry.utilities ? ["Followed by ".concat(usedByLabel(entry))] : [];
      return {
        key,
        heading,
        lines: [createdLine(entry), ...followed]
      };
    }
    if (kind === 'renamed') return {
      key,
      heading,
      lines: [renameLine(entry)].filter(Boolean)
    };
    if (kind === 'archived' || kind === 'restored') return {
      key,
      heading,
      lines: []
    };
    const reached = entry.utilities === null || entry.utilities === undefined ? [] : ["Changed ".concat(usedByLabel(entry))];
    return {
      key,
      heading,
      lines: [...fieldLines(entry.changes), renameLine(entry), scheduledLine(entry), ...reached, ...outcome].filter(Boolean)
    };
  });
};
const setupName = ref => {
  var _ref$name;
  return (_ref$name = ref === null || ref === void 0 ? void 0 : ref.name) !== null && _ref$name !== void 0 ? _ref$name : 'a setup that no longer exists';
};

/** The link's last change, as FR-15 words it: "Linked to Gas by Launch seed, Oct 24, 2026." Null when there's none. */
export const linkLine = history => {
  var _history$setup;
  const change = history === null || history === void 0 || (_history$setup = history.setup) === null || _history$setup === void 0 ? void 0 : _history$setup.lastChange;
  if (!change) return null;
  const what = change.value ? "".concat(change.whenAdded ? 'Added following' : 'Linked to', " ").concat(setupName(change.value)) : 'Left its setup';
  return "".concat(byLine(what, change.changedByName, change.changedOn), ".");
};

// The utility's own changes, in the order an item lists them.
const OPTION_LINES = [['setup', change => change.value ? "".concat(change.whenAdded ? 'Added following' : 'Linked to', " ").concat(setupName(change.value)) : 'Left its setup'], ['billSource', change => change.whenAdded ? "Added with bill source ".concat(sourceLabel(change.value)) : "Bill source: ".concat(sourceLabel(change.value))], ['apPush', change => "".concat(change.whenAdded ? 'Added with AP Push' : 'AP Push turned', " ").concat(onOff(change.value).toLowerCase())], ['billAutomation', change => "".concat(change.whenAdded ? 'Added with Bill Automation' : 'Bill Automation turned', " ").concat(onOff(change.value).toLowerCase())]];
const whoAndWhen = (name, on) => name ? "".concat(name, ", ").concat(dateLabel(on)) : dateLabel(on);

/**
 * The utility's own history (PRD V3 FR-15), newest first: its links to setups, its AP Push, Bill Automation and bill
 * source, and its moves that were scheduled and never applied. Changes written together, by one person at one time, are
 * one item; an option change that came through a setup names it, unless the item already says which setup it links to.
 */
export const utilityEntries = history => {
  var _history$scheduledLin;
  if (!history) return [];
  const groups = new Map();
  OPTION_LINES.forEach((_ref, rank) => {
    var _history$option$recen, _history$option;
    let _ref2 = _slicedToArray(_ref, 2),
      option = _ref2[0],
      wording = _ref2[1];
    ((_history$option$recen = (_history$option = history[option]) === null || _history$option === void 0 ? void 0 : _history$option.recentChanges) !== null && _history$option$recen !== void 0 ? _history$option$recen : []).forEach(change => {
      var _change$changedByName, _groups$get;
      const at = new Date(change.changedOn).getTime();
      const key = "".concat(at, "|").concat((_change$changedByName = change.changedByName) !== null && _change$changedByName !== void 0 ? _change$changedByName : '');
      const group = (_groups$get = groups.get(key)) !== null && _groups$get !== void 0 ? _groups$get : {
        key,
        at,
        heading: whoAndWhen(change.changedByName, change.changedOn),
        lines: []
      };
      group.lines.push({
        rank,
        option,
        text: wording(change),
        via: change.via
      });
      groups.set(key, group);
    });
  });
  const items = [...groups.values()].map(group => {
    const links = group.lines.some(line => line.option === 'setup');
    return {
      key: group.key,
      at: group.at,
      heading: group.heading,
      lines: group.lines.sort((a, b) => a.rank - b.rank).map(line => line.via && !links ? "".concat(line.text, ", via ").concat(setupName(line.via)) : line.text)
    };
  });
  ((_history$scheduledLin = history.scheduledLinks) !== null && _history$scheduledLin !== void 0 ? _history$scheduledLin : []).forEach((move, index) => {
    const verb = move.status === 'Failed' ? 'failed' : 'cancelled';
    items.push({
      key: "move|".concat(move.changedOn, "|").concat(index),
      at: new Date(move.changedOn).getTime(),
      heading: whoAndWhen(move.changedByName, move.changedOn),
      lines: ["Move to ".concat(setupName(move.setup), " from ").concat(dayLabel(move.effectiveFrom), " ").concat(verb), ...(move.outcome ? [move.outcome] : [])]
    });
  });
  return items.sort((a, b) => b.at - a.at).map(_ref3 => {
    let key = _ref3.key,
      heading = _ref3.heading,
      lines = _ref3.lines;
    return {
      key,
      heading,
      lines
    };
  });
};